import {
  type EnvironmentId,
  isManagedByAfar,
  type ModelSelection,
  type ProviderInstanceId,
} from "@afar-research/contracts";
import { useCallback, useMemo, useState } from "react";

import {
  defaultProjectCreationProviderOptions,
  describeProjectCreationInstanceIneligibility,
  describeProjectCreationProviderFallback,
  PENDING_PROJECT_CREATION_PROVIDER_SELECTION,
  type ProjectCreationProviderChoice,
  projectCreationFallbackEntries,
  type ProjectCreationProviderSelection,
  resolveProjectCreationModelSelection,
  resolveProjectCreationProviderSelection,
} from "../projectCreationProviderSelection";
import { useEnvironments } from "../state/environments";
import { useEnvironmentManagedByAfar } from "./useEnvironmentManagedByAfar";
import { useClientSettings } from "./useSettings";

/**
 * The provider a project dialog opens a thread on: the Settings default while
 * it can, and otherwise the ready instance the user picks in its place. Shared
 * by AFAR Project Creation and the Project Study, which ask the same question.
 *
 * Until the user picks, the choice is the default itself, so a default that
 * becomes ready while the dialog is open is used without another step.
 */
export function useProjectProviderChoice(environmentId: EnvironmentId | null) {
  const { environments } = useEnvironments();
  const clientSettings = useClientSettings();
  const managedByAfar = useEnvironmentManagedByAfar(environmentId);
  const [picked, setPicked] = useState<{
    readonly environmentId: EnvironmentId;
    readonly choice: ProjectCreationProviderChoice;
  } | null>(null);

  const serverConfig =
    environments.find((environment) => environment.environmentId === environmentId)?.serverConfig ??
    null;
  const selection: ProjectCreationProviderSelection = useMemo(
    () =>
      serverConfig === null
        ? PENDING_PROJECT_CREATION_PROVIDER_SELECTION
        : resolveProjectCreationProviderSelection({
            settings: { ...serverConfig.settings, ...clientSettings },
            providers: serverConfig.providers,
            managedByAfar: isManagedByAfar(serverConfig),
          }),
    [clientSettings, serverConfig],
  );
  const choice: ProjectCreationProviderChoice | null =
    picked !== null && picked.environmentId === environmentId
      ? picked.choice
      : selection.preselected === null
        ? null
        : {
            instanceId: selection.preselected.instanceId,
            model: selection.preselected.model,
            options: selection.preselected.options,
          };
  const modelSelection: ModelSelection | null = resolveProjectCreationModelSelection(
    selection,
    choice,
  );
  const providerFallbackReason = useMemo(
    () =>
      serverConfig === null
        ? null
        : describeProjectCreationProviderFallback(selection, {
            ...serverConfig.settings,
            ...clientSettings,
          }),
    [clientSettings, selection, serverConfig],
  );
  const eligibleInstanceIds = useMemo(
    () => new Set(selection.options.map((option) => option.instanceId)),
    [selection],
  );
  const fallbackInstanceEntries = useMemo(
    () => projectCreationFallbackEntries(selection),
    [selection],
  );
  const getDefaultProviderOptions = useCallback(
    (chosen: { readonly instanceId: ProviderInstanceId; readonly model: string }) =>
      defaultProjectCreationProviderOptions(selection, chosen),
    [selection],
  );
  const getInstanceDisabledReason = useCallback(
    (instanceId: ProviderInstanceId) =>
      describeProjectCreationInstanceIneligibility(selection, instanceId),
    [selection],
  );
  const onSelectionChange = useCallback(
    (next: ProjectCreationProviderChoice) => {
      if (environmentId !== null) setPicked({ environmentId, choice: next });
    },
    [environmentId],
  );
  const reset = useCallback(() => setPicked(null), []);

  return {
    modelSelection,
    reset,
    /** Everything a project dialog's provider section reads. */
    providerProps: {
      providerReadiness: selection.readiness,
      managedByAfar,
      instanceEntries: selection.instanceEntries,
      fallbackInstanceEntries,
      modelOptionsByInstance: selection.modelOptionsByInstance,
      eligibleInstanceIds,
      getInstanceDisabledReason,
      providerFallbackReason,
      selection: choice,
      onSelectionChange,
      getDefaultProviderOptions,
    },
  };
}
